import React, { useState, useEffect } from 'react'
import { Save, Edit, Upload } from 'lucide-react'
import { adminAPI } from '../../services/api'
import toast from 'react-hot-toast'

interface CompanyData {
  company_name: string
  nip: string
  regon: string
  krs: string
  street: string
  postal_code: string
  city: string
  country: string
  email: string
  phone: string
  bank_account: string
  bank_name: string
  invoice_prefix: string
  invoice_next_number: number
  vat_rate: number
  vat_rate_custom: number
  vat_eu_enabled: number
  currency: string
  logo_url: string
}

const CompanySettings: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [companyData, setCompanyData] = useState<CompanyData>({
    company_name: '',
    nip: '',
    regon: '',
    krs: '',
    street: '',
    postal_code: '',
    city: '',
    country: 'PL',
    email: '',
    phone: '',
    bank_account: '',
    bank_name: '',
    invoice_prefix: 'FV/',
    invoice_next_number: 1,
    vat_rate: 23,
    vat_rate_custom: 23,
    vat_eu_enabled: 0,
    currency: 'PLN',
    logo_url: ''
  })

  useEffect(() => {
    fetchCompanyData()
  }, [])

  const fetchCompanyData = async () => {
    try {
      const response = await adminAPI.getCompanyData()
      // Obsługa null wartości z API
      const apiData = response.data.data
      setCompanyData({
        company_name: apiData.company_name || '',
        nip: apiData.nip || '',
        regon: apiData.regon || '',
        krs: apiData.krs || '',
        street: apiData.street || '',
        postal_code: apiData.postal_code || '',
        city: apiData.city || '',
        country: apiData.country || 'PL',
        email: apiData.email || '',
        phone: apiData.phone || '',
        bank_account: apiData.bank_account || '',
        bank_name: apiData.bank_name || '',
        invoice_prefix: apiData.invoice_prefix || 'FV/',
        invoice_next_number: apiData.invoice_next_number || 1,
        vat_rate: apiData.vat_rate || 23,
        vat_rate_custom: apiData.vat_rate_custom || 23,
        vat_eu_enabled: apiData.vat_eu_enabled || 0,
        currency: apiData.currency || 'PLN',
        logo_url: apiData.logo_url || ''
      })
      setIsEditing(false)
    } catch (error) {
      console.error('Error fetching company data:', error)
      // Jeśli brak danych, używamy domyślnych i włączamy edycję
      setIsEditing(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    
    try {
      await adminAPI.updateCompanyData(companyData)
      toast.success('Dane firmy zapisane')
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving company data:', error)
      toast.error('Błąd zapisu danych')
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Sprawdź typ pliku
    if (!file.type.startsWith('image/')) {
      toast.error('Można przesłać tylko pliki graficzne')
      return
    }

    // Sprawdź rozmiar (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('Plik może mieć maksymalnie 500KB')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('logo', file)
      
      const response = await adminAPI.uploadLogo(formData)
      setCompanyData({...companyData, logo_url: response.data.logo_url})
      toast.success('Logo przesłane pomyślnie')
    } catch (error) {
      console.error('Error uploading logo:', error)
      toast.error('Błąd przesyłania logo')
    } finally {
      setUploading(false)
    }
  }

  const handleVatChange = (value: string) => {
    if (value === 'custom') {
      setCompanyData({...companyData, vat_rate: companyData.vat_rate_custom})
    } else {
      const rate = parseInt(value)
      setCompanyData({...companyData, vat_rate: rate, vat_rate_custom: rate})
    }
  }

  if (loading) {
    return <div className="text-white">Ładowanie...</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Dane Firmy</h1>
        <p className="text-gray-400">Dane używane do wystawiania faktur i dokumentów</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h3 className="text-lg font-semibold text-white mb-4">Podstawowe dane</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nazwa firmy
            </label>
            <input
              type="text"
              value={companyData.company_name}
              onChange={(e) => setCompanyData({...companyData, company_name: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={!isEditing}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              NIP
            </label>
            <input
              type="text"
              value={companyData.nip}
              onChange={(e) => setCompanyData({...companyData, nip: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="1234567890"
              disabled={!isEditing}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Stawka VAT (%)
            </label>
            <div className="space-y-2">
              <select
                value={[0, 5, 8, 23].includes(companyData.vat_rate) ? companyData.vat_rate.toString() : 'custom'}
                onChange={(e) => handleVatChange(e.target.value)}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={!isEditing}
              >
                <option value="23">23% (standardowa)</option>
                <option value="8">8% (obniżona)</option>
                <option value="5">5% (obniżona)</option>
                <option value="0">0% (zwolniona)</option>
                <option value="custom">Inna stawka</option>
              </select>
              {![0, 5, 8, 23].includes(companyData.vat_rate) && (
                <input
                  type="number"
                  value={companyData.vat_rate}
                  onChange={(e) => setCompanyData({...companyData, vat_rate: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Wprowadź stawkę VAT"
                  min="0"
                  max="100"
                  disabled={!isEditing}
                  required
                />
              )}
            </div>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Logo firmy</h3>
        
        <div className="mb-6">
          <div className="flex items-center space-x-4">
            {companyData.logo_url && (
              <div className="w-32 h-16 border border-dark-500 rounded-lg overflow-hidden">
                <img 
                  src={companyData.logo_url} 
                  alt="Logo firmy" 
                  className="w-full h-full object-contain bg-white"
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={!isEditing || uploading}
                className="hidden"
              />
              <label
                htmlFor="logo-upload"
                className={`inline-flex items-center space-x-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                  !isEditing || uploading 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Upload className="h-4 w-4" />
                <span>{uploading ? 'Przesyłanie...' : 'Upload Logo'}</span>
              </label>
              <p className="text-xs text-gray-400 mt-2">
                PNG/JPG, max 500KB, zalecane: 200x100px
              </p>
            </div>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Adres</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Ulica i numer
            </label>
            <input
              type="text"
              value={companyData.street}
              onChange={(e) => setCompanyData({...companyData, street: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={!isEditing}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Kod pocztowy
            </label>
            <input
              type="text"
              value={companyData.postal_code}
              onChange={(e) => setCompanyData({...companyData, postal_code: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="00-000"
              disabled={!isEditing}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Miasto
            </label>
            <input
              type="text"
              value={companyData.city}
              onChange={(e) => setCompanyData({...companyData, city: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={!isEditing}
              required
            />
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Kontakt</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={companyData.email}
              onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Telefon
            </label>
            <input
              type="tel"
              value={companyData.phone}
              onChange={(e) => setCompanyData({...companyData, phone: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="+48 123 456 789"
              required
            />
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Dane bankowe</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nazwa banku
            </label>
            <input
              type="text"
              value={companyData.bank_name}
              onChange={(e) => setCompanyData({...companyData, bank_name: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Numer konta
            </label>
            <input
              type="text"
              value={companyData.bank_account}
              onChange={(e) => setCompanyData({...companyData, bank_account: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="00 0000 0000 0000 0000 0000 0000"
              required
            />
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Numeracja faktur</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Prefiks faktur
            </label>
            <input
              type="text"
              value={companyData.invoice_prefix}
              onChange={(e) => setCompanyData({...companyData, invoice_prefix: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="FV/"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Następny numer faktury
            </label>
            <input
              type="number"
              value={companyData.invoice_next_number}
              onChange={(e) => setCompanyData({...companyData, invoice_next_number: parseInt(e.target.value)})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              min="1"
              required
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Edit className="h-4 w-4" />
              <span>Edytuj</span>
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  fetchCompanyData()
                  setIsEditing(false)
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                <span>{saving ? 'Zapisywanie...' : 'Zapisz'}</span>
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  )
}

export default CompanySettings